<template>
  <button type="button" class="announcement-card" @click="emit('open')">
    <AnnouncementIcon class="announcement-icon" />
    <span class="announcement-content">{{ announcementText }}</span>
  </button>
</template>

<script setup lang="ts">
/**
 * Purpose: Present the current-language announcement as a compact floating pill.
 * Props: announcement may omit locales; missing content renders as an empty single line.
 * Emits: open requests the owning chat dialog to show the complete announcement.
 */
import { computed, type PropType } from 'vue'
import { useLang } from '../../../hooks/useLang'
import AnnouncementIcon from '../../icon/AnnouncementIcon.vue'

type SupportedLanguage = 'zh-CN' | 'zh-TW' | 'en-US'

const props = defineProps({
  announcement: {
    type: Object as PropType<Partial<Record<SupportedLanguage, string>>>,
    default: () => ({}),
  },
})

const emit = defineEmits<{
  (event: 'open'): void
}>()

const { getUCLang } = useLang()

const announcementText = computed(() => {
  const localizedText = props.announcement[getUCLang()] ?? ''
  return localizedText.split('\n')[0] || localizedText
})
</script>

<style lang="less" scoped>
.announcement-card {
  display: flex;
  width: 100%;
  height: 36px;
  min-width: 0;
  padding: 8px 12px;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--abot-brand-border);
  border-radius: 100px;
  background: var(--abot-brand-surface);
  color: var(--abot-brand-color);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-sizing: border-box;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.announcement-card:focus-visible {
  outline: 2px solid var(--abot-brand-color);
  outline-offset: 2px;
}

.announcement-icon {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.announcement-content {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: currentColor;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
